import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, ArrowRight, Phone, ChevronDown } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { waLink, telLink, track } from "../../lib/site";

const NAV_BEFORE = [{ to: "/", label: "Home" }];
const NAV_AFTER = [
  { to: "/calculators/emi", label: "EMI Calculator" },
  { to: "/calculators/eligibility", label: "Eligibility" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

function ProductsDropdown() {
  const { products } = useSite();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);

  return (
    <div className="relative" ref={ref}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" data-testid="nav-our-products" aria-expanded={open} aria-haspopup="true"
        onClick={() => setOpen(true)}
        className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 inline-flex items-center gap-1 ${open ? "text-brand-red bg-brand-red/5" : "text-brand-dark/70 hover:text-brand-dark"}`}>
        Our Products <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="our-products-dropdown"
          className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[480px] max-w-[90vw] rounded-2xl bg-white border border-black/5 shadow-xl p-4 z-50">
          <div className="grid grid-cols-2 gap-1">
            {products.map((p) => (
              <Link key={p.id} to={`/loans/${p.slug}`} data-testid={`dropdown-product-${p.slug}`}
                onClick={() => setOpen(false)}
                className="rounded-xl px-4 py-3 transition-colors duration-150 hover:bg-brand-cream group">
                <p className="font-heading font-bold text-sm text-brand-dark group-hover:text-brand-red transition-colors duration-150">{p.name}</p>
                <p className="text-xs text-brand-muted mt-0.5 line-clamp-1">{p.tagline}</p>
              </Link>
            ))}
          </div>
          <Link to="/loans" data-testid="dropdown-view-all" onClick={() => setOpen(false)}
            className="mt-3 pt-3 border-t border-black/5 flex items-center justify-center gap-1.5 text-xs font-bold text-brand-red hover:opacity-80">
            View All Products <ArrowRight size={13} />
          </Link>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { settings, products } = useSite();
  const [open, setOpen] = useState(false);
  const [mobileProducts, setMobileProducts] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMobileProducts(false); }, [location.pathname]);

  const navLink = (n, mobile = false) => (
    <NavLink key={n.to} to={n.to} end={n.to === "/"}
      data-testid={`${mobile ? "mobile-nav" : "nav"}-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => mobile
        ? `px-4 py-3 rounded-xl text-sm font-semibold ${isActive ? "text-brand-red bg-brand-red/5" : "text-brand-dark/70"}`
        : `px-3.5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${isActive ? "text-brand-red bg-brand-red/5" : "text-brand-dark/70 hover:text-brand-dark"}`}>
      {n.label}
    </NavLink>
  );

  return (
    <header data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-shadow duration-300 ${scrolled ? "bg-white/80 backdrop-blur-xl shadow-sm border-black/5" : "bg-brand-cream/90 backdrop-blur-md border-black/5"}`}>
      <div className="container-site flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="header-logo" className="flex items-center" aria-label="BharatBest Fintech home">
          <img src="/assets/bharatbest-logo.png" alt="BharatBest Fintech Private Limited" className="h-7 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_BEFORE.map((n) => navLink(n))}
          <ProductsDropdown />
          {NAV_AFTER.map((n) => navLink(n))}
        </nav>

        <div className="flex items-center gap-2.5">
          <a href={telLink(settings)} data-testid="header-call-btn" onClick={() => track("phone_click")}
            aria-label="Call BharatBest Fintech"
            className="hidden sm:flex w-10 h-10 rounded-full border border-black/10 bg-white items-center justify-center text-brand-red transition-transform duration-200 hover:-translate-y-0.5">
            <Phone size={17} />
          </a>
          <a href={waLink(settings)} target="_blank" rel="noreferrer" data-testid="header-whatsapp-btn" onClick={() => track("whatsapp_click")}
            aria-label="Chat on WhatsApp"
            className="hidden sm:flex w-10 h-10 rounded-full border border-black/10 bg-white items-center justify-center text-[#1FA855] transition-transform duration-200 hover:-translate-y-0.5">
            <MessageCircle size={18} />
          </a>
          <Link to="/apply" data-testid="header-apply-btn" className="btn-primary !px-5 !py-2.5">
            Apply Now <ArrowRight size={15} />
          </Link>
          <button data-testid="mobile-menu-toggle" aria-label="Toggle menu" aria-expanded={open} onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 rounded-full border border-black/10 bg-white flex items-center justify-center text-brand-dark">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-black/5 bg-white/95 backdrop-blur-xl max-h-[75vh] overflow-y-auto" aria-label="Mobile">
          <div className="container-site py-4 flex flex-col gap-1">
            {NAV_BEFORE.map((n) => navLink(n, true))}
            <button type="button" data-testid="mobile-nav-our-products" aria-expanded={mobileProducts}
              onClick={() => setMobileProducts((v) => !v)}
              className={`px-4 py-3 rounded-xl text-sm font-semibold text-left flex items-center justify-between ${mobileProducts ? "text-brand-red bg-brand-red/5" : "text-brand-dark/70"}`}>
              Our Products <ChevronDown size={15} className={`transition-transform duration-200 ${mobileProducts ? "rotate-180" : ""}`} />
            </button>
            {mobileProducts && (
              <div className="ml-4 pl-4 border-l-2 border-brand-red/15 flex flex-col gap-0.5" data-testid="mobile-products-list">
                {products.map((p) => (
                  <NavLink key={p.id} to={`/loans/${p.slug}`} data-testid={`mobile-product-${p.slug}`}
                    className="px-3 py-2.5 rounded-lg text-sm font-semibold text-brand-dark/70 hover:text-brand-red">
                    {p.name}
                  </NavLink>
                ))}
                <NavLink to="/loans" data-testid="mobile-products-all"
                  className="px-3 py-2.5 rounded-lg text-sm font-bold text-brand-red">
                  View All Products →
                </NavLink>
              </div>
            )}
            {NAV_AFTER.map((n) => navLink(n, true))}
            <a href={waLink(settings)} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-btn" onClick={() => track("whatsapp_click")}
              className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#1FA855] text-white text-sm font-bold px-4 py-3">
              <MessageCircle size={16} /> WhatsApp Us
            </a>
            <a href={telLink(settings)} data-testid="mobile-call-btn" onClick={() => track("phone_click")}
              className="mt-2 flex items-center justify-center gap-2 rounded-full border border-brand-red/30 text-brand-red text-sm font-bold px-4 py-3">
              <Phone size={16} /> Call {settings.phone_display || settings.phone}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
